import { useState } from "react";
import type { JobDetailDto } from "@grizzly/shared";
import { api, jobPdfUrl } from "../../lib/apiClient.js";
import type { ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";
import { CheckCircle2, FileText, PauseCircle, PlayCircle } from "lucide-react";

export function JobActionBar({
  job,
  onChanged,
}: {
  job: JobDetailDto;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState("");

  const canRelease = job.status === "planned";
  const canHold = ["released", "in_progress", "ready_for_delivery"].includes(job.status);
  const canResume = job.status === "on_hold";
  const canReady = ["released", "in_progress"].includes(job.status);
  const canComplete = job.status === "ready_for_delivery";
  const canCancel = ["draft", "planned", "released", "in_progress", "on_hold"].includes(
    job.status
  );

  const act = async (fn: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await fn();
      push(success);
      onChanged();
    } catch (err) {
      const e = err as ApiClientError;
      push(
        e.message,
        e.requestId ? t("common.requestId", { id: e.requestId }) : undefined,
        "danger"
      );
    } finally {
      setBusy(false);
    }
  };

  const submitHold = async () => {
    setBusy(true);
    try {
      await api.holdJob(job.id, holdReason);
      push(t("jobs.toast.hold"));
      setHoldOpen(false);
      setHoldReason("");
      onChanged();
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="secondary" asChild>
        <a href={jobPdfUrl(job.id)} target="_blank" rel="noreferrer">
          <FileText className="h-4 w-4" aria-hidden="true" /> {t("jobs.action.traveler")}
        </a>
      </Button>
      {canRelease ? (
        <Button
          variant="primary"
          disabled={busy}
          onClick={() => void act(() => api.releaseJob(job.id), t("jobs.toast.released"))}
        >
          {t("jobs.action.release")}
        </Button>
      ) : null}
      {canHold ? (
        <Button variant="secondary" disabled={busy} onClick={() => setHoldOpen(true)}>
          <PauseCircle className="h-4 w-4" aria-hidden="true" /> {t("jobs.action.hold")}
        </Button>
      ) : null}
      {canResume ? (
        <Button
          variant="primary"
          disabled={busy}
          onClick={() => void act(() => api.resumeJob(job.id), t("jobs.toast.resumed"))}
        >
          <PlayCircle className="h-4 w-4" aria-hidden="true" /> {t("jobs.action.resume")}
        </Button>
      ) : null}
      {canReady ? (
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => void act(() => api.markJobReady(job.id), t("jobs.toast.ready"))}
        >
          {t("jobs.action.ready")}
        </Button>
      ) : null}
      {canComplete ? (
        <Button
          variant="primary"
          disabled={busy}
          onClick={() =>
            void act(() => api.completeJob(job.id, false), t("jobs.toast.completed"))
          }
        >
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />{" "}
          {t("jobs.action.complete")}
        </Button>
      ) : null}
      {canCancel ? (
        <Button variant="danger" disabled={busy} onClick={() => setCancelOpen(true)}>
          {t("common.cancel")}
        </Button>
      ) : null}

      <Modal
        open={holdOpen}
        onOpenChange={setHoldOpen}
        title={t("jobs.holdModal.title")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setHoldOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              disabled={busy || holdReason.trim().length < 3}
              onClick={submitHold}
            >
              {t("jobs.holdModal.confirm")}
            </Button>
          </>
        }
      >
        <TextInput
          label={t("common.fields.reason")}
          value={holdReason}
          onChange={(e) => setHoldReason(e.target.value)}
          hint={t("jobs.holdModal.hint")}
          error={null}
        />
      </Modal>

      <Modal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t("jobs.cancelModal.title")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              {t("jobs.cancelModal.keep")}
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() =>
                void act(
                  () => api.cancelJob(job.id, cancelNote || undefined),
                  t("jobs.toast.cancelled")
                )
              }
            >
              {t("jobs.cancelModal.confirm")}
            </Button>
          </>
        }
      >
        <TextInput
          label={t("jobs.cancelModal.reason")}
          value={cancelNote}
          onChange={(e) => setCancelNote(e.target.value)}
          error={null}
        />
      </Modal>
    </>
  );
}
